import React, { memo, useEffect, useRef, useState } from 'react';
import { Link } from 'gatsby';
import { motion, useReducedMotion } from 'framer-motion';
import cx from 'classnames';

import { PlayIcon } from '../Icons';

import { ITrailer } from '../../utils/models';

import './TrailerGridItem.scss';

export interface ITrailerGridItemProps {
  item: ITrailer;
  listName: string;
  onClick?: (trailer: ITrailer) => void;
  isActive?: boolean;
  linkState?: any;
}

const TrailerGridItem = ({
  item,
  listName,
  onClick = () => {},
  isActive = false,
  linkState = null,
}: ITrailerGridItemProps) => {
  const { title, id } = item;
  const baseImageUrl = `https://cdnimg.spectrum.net/imageserver/program/${id}?twccategory=Hero`;
  const [loaded, setLoaded] = useState<boolean>(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const itemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (imageRef.current?.complete) {
      setLoaded(true);
    }
  }, [imageRef]);

  const reducedMotion = useReducedMotion();

  const yPos = reducedMotion ? 0 : 24;
  const animationVariants = {
    enter: {
      y: yPos,
      opacity: 0,
    },
    center: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        type: 'tween',
        ease: [0.33, 1, 0.68, 1],
      },
    },
    exit: {
      y: yPos,
      opacity: 0,
      transition: {
        duration: 0.4,
        type: 'tween',
        ease: [0.32, 0, 0.67, 0],
      },
    },
  };

  return (
    <motion.li
      variants={animationVariants}
      ref={itemRef}
      className={cx({
        'trailer-grid-item': true,
        'trailer-grid-item--active': isActive && reducedMotion,
      })}
      style={{ zIndex: isActive ? 5 : 10 }}
    >
      {isActive && !reducedMotion && (
        <motion.div
          layoutId="outline"
          className="trailer-grid-item__outline"
          initial={false}
          transition={{
            type: 'spring',
            stiffness: 150,
            damping: 25,
            velocity: 3,
            duration: 0.3,
          }}
        />
      )}

      <Link
        to={`/trailers/watch/?v=${id}&list=${listName}`}
        state={linkState}
        aria-label={`Watch trailer for ${title}`}
        title={`Watch trailer for ${title}`}
        id={`Watch trailer for ${title}`}
        onClick={() => onClick(item)}
      >
        <figure>
          <div className="trailer-grid-item__image-wrapper">
            {!loaded && (
              <motion.div
                role="presentation"
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="trailer-grid-item__image-placeholder"
              />
            )}
            <picture>
              <source
                srcSet={`${baseImageUrl}&width=550`}
                media="(min-width: 1025px)"
              />
              <source
                srcSet={`${baseImageUrl}&width=450`}
                media="(min-width: 501px) and (max-width: 1024px)"
              />
              <source
                srcSet={`${baseImageUrl}&width=400`}
                media="(min-width: 0px) and (max-width: 500px)"
              />
              <img
                width="400"
                height="225"
                src={`${baseImageUrl}&width=400`}
                alt={title}
                loading="lazy"
                ref={imageRef}
                onLoad={() => setLoaded(true)}
                className={cx({
                  'trailer-grid-item__image': true,
                  'trailer-grid-item__image--loaded': loaded,
                  'trailer-grid-item__image--loading': !loaded,
                })}
              />
              <PlayIcon />
            </picture>
          </div>
          <figcaption className="trailer-grid-item__caption">
            {title}
          </figcaption>
        </figure>
      </Link>
    </motion.li>
  );
};

export default memo(TrailerGridItem);
